/** 261104 E1-P1 — thân bốn cổng duyệt: Chọn kết · Cải biên · Khung · Chia tập. Chỉ hiển thị; mọi hành động đi qua props. Không import antd. */
import React, { useState } from 'react';
import type {
  ChangeItemData, ChangesDataView, EndingDataView, EpisodesDataView, SkillChangeChoice, SkillChangeGroup, SpineDataView,
} from '../../../types/skillAdaptation';
import { Ms, Tag } from './SkillParts';
import {
  applySkipLabel, chapterRangesOf, countApplySkip, durationClassOf, durationLabelOf, durationScaleSecOf, overlongTagOf, sourceLabelOf, totalDurationLabelOf,
} from './skillAdaptUtils';
import { changesMapsOf, spineGroupsOf } from './skillWritingUtils';

const FullText: React.FC<{ text: string }> = ({ text }) => (text.trim() ? <details className="fd"><summary>Xem bản đầy đủ máy đã viết</summary><p style={{ whiteSpace: 'pre-wrap' }}>{text}</p></details> : null);

// ── Cổng 1 · Chọn kết ──────────────────────────────────────────────────────────────────────────
export const EndingBody: React.FC<{ data: EndingDataView; text: string; names: Record<string, string>; optionId: string | null; onPick: (id: string) => void; readOnly: boolean }> = ({ data, text, names, optionId, onPick, readOnly }) => {
  const options = data.options ?? [];
  const cmp = data.comparison;
  const basis = (o: (typeof options)[number]): string => {
    const b = o.basisChapters; if (!b) return '';
    return [['Kết', b.ending], ['Chuẩn bị', b.setup], ['Hậu quả', b.aftermath]].map(([k, v]) => { const s = chapterRangesOf(v as number[] | undefined); return s ? `${k}: ch.${s}.` : ''; }).filter(Boolean).join(' ');
  };
  return (
    <>
      {data.coverageSentence ? <p className="graynote">{data.coverageSentence}</p> : null}
      {options.map((o) => {
        const ms = o.endingMilestone;
        const rows: Array<[string, React.ReactNode]> = [
          ['Mốc kết chính xác', ms?.id || ms?.shortName ? <Ms text={[ms?.id, ms?.shortName, ms?.chapter ? `(cuối ch.${ms.chapter})` : ''].filter(Boolean).join(' · ')} names={names} /> : ''],
          ['Chương căn cứ', basis(o)], ['Phần hậu quả cần giữ', <Ms key="k" text={o.keep} names={names} />], ['Ước quy mô', o.scale ?? ''], ['Chi phí sửa', <Ms key="f" text={o.fixCost} names={names} />], ['Đánh đổi', o.tradeoff ?? ''],
        ];
        return (
          <div className="choice" key={o.id}>
            <label className="chead">
              <input type="radio" name="skill-ending" value={o.id} checked={optionId === o.id} disabled={readOnly} onChange={() => onPick(o.id)} />
              <b>{o.title}</b>{o.recommended ? <span className="chip ok">Khuyến nghị</span> : null}
            </label>
            <p className="oneline"><Ms text={o.summary} names={names} /></p>
            <details className="fd" open><summary>Chi tiết</summary>
              <dl className="kv">{rows.filter(([, v]) => v !== '' && v !== null).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
            </details>
          </div>
        );
      })}
      {cmp?.criteria?.length && cmp.rows?.length ? (
        <details className="fd"><summary>Xem so sánh</summary>
          {data.promise ? <><p className="sub">Lời hứa của bộ phim</p><p>{data.promise}</p></> : null}
          <table className="t"><thead><tr><th>Mặt</th>{cmp.rows.map((r) => <th key={r.optionId}>{options.find((o) => o.id === r.optionId)?.title ?? r.optionId}</th>)}</tr></thead>
            <tbody>{cmp.criteria.map((c, i) => <tr key={c}><td><b>{c}</b></td>{cmp.rows!.map((r) => <td key={r.optionId}>{r.values?.[i] ?? ''}</td>)}</tr>)}</tbody></table>
          <p className="note">Số tập chỉ ước từ chuỗi cảnh, không quy đổi từ số chương và chưa phải thời lượng đã đo.</p>
        </details>
      ) : null}
      <FullText text={text} />
    </>
  );
};

// ── Cổng 2 · Cải biên ──────────────────────────────────────────────────────────────────────────
const GROUPS: Array<[SkillChangeGroup, string, string]> = [
  ['RELATIONSHIP', 'Quan hệ & tình cảm', 'Phát triển quan hệ qua sự việc và lựa chọn; giữ động cơ, cam kết và tri thức.'],
  ['ANTAGONIST_FINALE', 'Đối kháng & cao trào cuối', 'Đối thủ gây áp lực thật; nhân vật chính quyết định phần cốt yếu của kết quả.'],
  ['LOCALIZATION', 'Bản địa hoá', 'Tên, thế giới và thuật ngữ theo chính sách bạn đã chọn.'],
];
const KIND_LABEL: Record<string, string> = { NAME: 'Tên', PLACE: 'Nơi chốn', TERM: 'Thuật ngữ', WORLD: 'Thế giới' };

const ChangeRow: React.FC<{ c: ChangeItemData; choice: SkillChangeChoice; fresh: boolean; names: Record<string, string>; readOnly: boolean; onChoose: (id: string, v: SkillChangeChoice) => void }> = ({ c, choice, fresh, names, readOnly, onChoose }) => {
  const skip = choice === 'SKIP';
  return (
    <article className={`crow ${skip ? 'skip' : ''} ${fresh ? 'fresh' : ''}`} data-row={c.id}>
      <header>
        <span className="id">{c.id}</span><Tag kind={c.kind} /><h3><Ms text={c.title} names={names} /></h3>
        {fresh ? <span className="chip blue">Mới sinh · xem kỹ</span> : null}
        <span className={`chip ${skip ? '' : 'blue'}`}>{skip ? 'Đã bỏ' : readOnly ? 'Đã chọn' : 'Sẽ áp dụng'}</span>
        {readOnly ? null : (
          <span className="rowbtns">
            <button type="button" className={`btn sm ${skip ? '' : 'on'}`} aria-pressed={!skip} onClick={() => onChoose(c.id, 'APPLY')}>Áp dụng</button>
            <button type="button" className={`btn sm ${skip ? 'on off' : ''}`} aria-pressed={skip} onClick={() => onChoose(c.id, 'SKIP')}>Bỏ</button>
          </span>
        )}
      </header>
      {c.proposal ? <p className="chg"><span className="k">Thay đổi</span><Ms text={c.proposal} names={names} /></p> : null}
      <details className="fd"><summary>Xem chi tiết</summary>
        <dl className="kv">
          <div><dt>Vấn đề + căn cứ nguồn</dt><dd><Ms text={c.problem} names={names} /></dd></div><div><dt>Giá trị mong muốn</dt><dd><Ms text={c.benefit} names={names} /></dd></div>
          <div><dt>Mốc gieo / sửa / bỏ & hệ quả</dt><dd><Ms text={c.affectedMilestones} names={names} /></dd></div><div><dt>Đánh đổi</dt><dd><Ms text={c.cost} names={names} /></dd></div>
        </dl>
      </details>
    </article>
  );
};

export const ChangesBody: React.FC<{
  data: ChangesDataView; text: string; names: Record<string, string>; choices: Record<string, SkillChangeChoice>; readOnly: boolean; freshIds: string[];
  onChoose: (id: string, v: SkillChangeChoice) => void; onBulk: (mode: 'all' | 'noNew') => void; onWantChange: () => void;
}> = ({ data, text, names, choices, readOnly, freshIds, onChoose, onBulk, onWantChange }) => {
  const all = data.changes ?? []; const nNew = all.filter((c) => c.kind === 'NEW').length; const fresh = new Set(freshIds);
  const table = data.localizeTable ?? [];
  const maps = changesMapsOf(data);   // E1-P2 (v2): hai bảng chỉ hiện khi lượt có dữ liệu; lượt cũ ⇒ rỗng ⇒ bảng phẳng P1
  return (
    <>
      <div className="topline">
        <span><b>{all.length} thay đổi</b>{nNew ? <> · <b>{nNew}</b> sáng tác mới cần xem kỹ</> : null}</span>
        <span className="legend" style={{ margin: '0 auto 0 4px' }}><Tag kind="src" /><Tag kind="MERGED" /><Tag kind="NEW" /></span>
        {readOnly || !all.length ? null : <><button type="button" className="btn" onClick={() => onBulk('all')}>Áp dụng tất cả</button><button type="button" className="btn" onClick={() => onBulk('noNew')}>Chỉ giữ Nguồn & Gộp</button></>}
      </div>
      {GROUPS.map(([g, title]) => {
        const rows = all.filter((c) => c.group === g); const keep = (data.keepGroups ?? []).find((k) => k.group === g);
        return (
          <React.Fragment key={g}>
            <div className="group"><h3>{title}</h3><span className={`chip ${rows.length ? 'blue' : 'ok'}`}>{rows.length ? `${rows.length} thay đổi` : 'Giữ nguyên'}</span></div>
            {rows.length ? rows.map((c) => <ChangeRow key={c.id} c={c} choice={choices[c.id] ?? 'APPLY'} fresh={fresh.has(c.id)} names={names} readOnly={readOnly} onChoose={onChoose} />) : (
              <div className="keepcard">
                <div className="row between"><b>Nguồn đã đủ — giữ nguyên</b>{readOnly ? null : <button type="button" className="btn" onClick={onWantChange}>Vẫn muốn thay đổi</button>}</div>
                {keep?.reason ? <details className="fd"><summary>Xem căn cứ</summary><p className="muted"><Ms text={keep.reason} names={names} /></p></details> : null}
              </div>
            )}
            {g === 'RELATIONSHIP' && (maps.relationships.length || maps.assessment) ? (
              <details className="mini" data-testid="map-relationship"><summary>Bản đồ quan hệ theo thời gian (kèm phần cải biên)</summary>
                <div className="inner">
                  {maps.relationships.length ? (
                    <table className="t"><thead><tr><th>Quan hệ</th><th>Vai trò</th><th>Đầu → cuối</th><th>Chặng theo mốc</th><th>Cam kết &amp; tri thức</th></tr></thead>
                      <tbody>{maps.relationships.map((r, i) => <tr key={`${r.pair}-${i}`}><td><b>{r.pair}</b></td><td>{r.role}</td><td>{r.arc}</td><td><Ms text={r.stages} names={names} /></td><td>{r.commitmentKnowledge}</td></tr>)}</tbody></table>
                  ) : null}
                  {maps.assessment ? <p className="note">Đánh giá nguồn: <Ms text={maps.assessment} names={names} /></p> : null}
                </div>
              </details>
            ) : null}
            {g === 'ANTAGONIST_FINALE' && maps.antagonist ? (
              <details className="mini" data-testid="map-antagonist"><summary>Thử thách cuối (đối kháng)</summary>
                <div className="inner"><dl className="kv">{maps.antagonist.map((r) => <div key={r.label}><dt>{r.label}</dt><dd><Ms text={r.value} names={names} /></dd></div>)}</dl></div>
              </details>
            ) : null}
            {g === 'LOCALIZATION' && table.length ? (
              <details className="mini"><summary>Bảng người / nơi và thuật ngữ{data.localizeMode === 'OFF' ? ' (chính sách: không đổi tên và thế giới)' : ''}</summary>
                <div className="inner"><table className="t"><thead><tr><th>Loại</th><th>Tên gốc → tên đích</th><th>Ghi chú</th></tr></thead>
                  <tbody>{table.map((r, i) => <tr key={`${r.source}-${i}`}><td>{KIND_LABEL[r.kind] ?? r.kind}</td><td>{r.source} → {r.target}</td><td>{r.note ?? ''}</td></tr>)}</tbody></table>
                  <p className="note">Kịch bản sạch chỉ dùng tên đích; bảng này nằm ngoài thân kịch bản.</p></div>
              </details>
            ) : null}
          </React.Fragment>
        );
      })}
      <FullText text={text} />
    </>
  );
};
export const changeCountsLabel = (choices: Record<string, SkillChangeChoice>): string => applySkipLabel(countApplySkip(choices));

// ── Cổng 3 · Khung câu chuyện ──────────────────────────────────────────────────────────────────
export const SpineBody: React.FC<{ data: SpineDataView; text: string; names: Record<string, string> }> = ({ data, text, names }) => {
  const ms = data.milestones ?? []; const nNew = ms.filter((m) => m.label === 'NEW').length;
  const groups = spineGroupsOf(data);   // E1-P2 (v2): nhóm theo cụm; thiếu cụm ⇒ null ⇒ bảng phẳng P1
  const mref = (m: (typeof ms)[number]): React.ReactNode => <span className="mref"><span>{m.shortName}</span><span className="mcode" title={`Mốc ${m.id} trong khung (mã nội bộ)`}>{m.id.replace(/B(\d)/, 'B​$1')}</span></span>;
  return (
    <>
      <div className="topline"><span><b>{ms.length} mốc</b> · <b>{nNew} mốc mới</b> cần xem kỹ</span><span className="legend" style={{ margin: '0 auto 0 4px' }}><Tag kind="SOURCE" /><Tag kind="MERGED" /><Tag kind="NEW" /></span></div>
      {groups ? groups.map((g) => (
        <React.Fragment key={g.id || 'rest'}>
          <div className="group" data-cluster={g.id}><h3>{g.id ? `${g.id} · ` : ''}{g.name}</h3>{g.range ? <span className="chip">{g.range}</span> : null}<span className="chip blue">{g.milestones.length} mốc</span></div>
          <table className="t">
            <thead><tr><th style={{ width: 200 }}>Mốc</th><th style={{ width: 90 }}>Nguồn</th><th>Trước → tác nhân/lựa chọn → sau</th><th style={{ width: 220 }}>Hệ quả nối tiếp</th><th style={{ width: 150 }}>Chức năng / trọng lượng</th></tr></thead>
            <tbody>{g.milestones.map((m) => (
              <tr key={m.id}><td>{mref(m)}</td><td><Tag kind={m.label} /><div className="small">{sourceLabelOf(m.sourceChapters)}</div></td><td><Ms text={m.summary} names={names} /></td><td><Ms text={m.consequence} names={names} /></td><td>{m.weight ?? ''}</td></tr>
            ))}</tbody>
          </table>
        </React.Fragment>
      )) : (
      <table className="t">
        <thead><tr><th style={{ width: 230 }}>Mốc</th><th style={{ width: 96 }}>Nguồn</th><th>Tóm tắt (trước → tác nhân/lựa chọn → sau)</th></tr></thead>
        <tbody>{ms.map((m) => (
          <tr key={m.id}>
            <td><span className="mref"><span>{m.shortName}</span><span className="mcode" title={`Mốc ${m.id} trong khung (mã nội bộ)`}>{m.id.replace(/B(\d)/, 'B​$1')}</span></span></td>
            <td><Tag kind={m.label} /><div className="small">{sourceLabelOf(m.sourceChapters)}</div></td>
            <td><Ms text={m.summary} names={names} /></td>
          </tr>
        ))}</tbody>
      </table>
      )}
      <FullText text={text} />
    </>
  );
};

// ── Cổng 4 · Chia tập ──────────────────────────────────────────────────────────────────────────
export const SplitBody: React.FC<{ data: EpisodesDataView; text: string; names: Record<string, string>; capSec: number; overlong: number[]; kept: number[]; disabled: boolean; onResplit: () => void }> = ({ data, text, names, capSec, overlong, kept, disabled, onResplit }) => {
  const eps = data.episodes ?? []; const [open, setOpen] = useState<Record<number, boolean>>({});
  const over = new Set(overlong); const capMin = String(capSec / 60).replace('.', ','); const scaleSec = durationScaleSecOf(capSec);
  return (
    <>
      <div className="topline"><span><b>{eps.length} tập</b> · tổng ước {totalDurationLabelOf(eps)} phút · trần {capMin} phút/tập</span><span className="legend" style={{ margin: '0 auto 0 4px' }}>bấm một dòng để xem cảnh chính, nguồn, căn cứ ước</span></div>
      <table className="t split">
        <thead><tr><th style={{ width: 64 }}>Tập</th><th>Tóm tắt</th><th style={{ width: 230 }}>Ước (phút)</th></tr></thead>
        <tbody>{eps.map((e, i) => {
          const lo = e.estLowSec ?? e.estHighSec; const hi = e.estHighSec; const o = over.has(e.n); const isOpen = !!open[e.n];
          return (
            <React.Fragment key={e.n}>
              <tr className={`xr ${o ? 'over' : ''}`} onClick={() => setOpen({ ...open, [e.n]: !isOpen })} aria-expanded={isOpen}>
                <td className="id"><span className="caret">{isOpen ? '▾' : '▸'}</span> {String(i + 1).padStart(2, '0')}</td>
                <td><Ms text={e.title ?? ''} names={names} />
                  {o ? <div className="cause"><span className="tag bad">{overlongTagOf(lo, hi, capSec)}</span>{kept.includes(e.n) ? ' · bạn chọn giữ nguyên' : ''}<br /><button type="button" className="btn primary sm" disabled={disabled} onClick={(ev) => { ev.stopPropagation(); onResplit(); }}>Máy chia lại / nén tập này</button></div> : null}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}><span className={`dur ${durationClassOf(lo, hi, capSec)}`}><i style={{ left: `${(lo / scaleSec) * 100}%`, width: `${Math.max(2, ((hi - lo) / scaleSec) * 100)}%` }} /></span><b>{durationLabelOf(e.estLowSec, hi)}</b></td>
              </tr>
              {isOpen ? (
                <tr className="det"><td /><td colSpan={2}><dl className="kv">
                  <div><dt>Cảnh chính</dt><dd><Ms text={e.scenes} names={names} /></dd></div><div><dt>Nguồn</dt><dd>{sourceLabelOf(e.sourceChapters)}</dd></div><div><dt>Căn cứ ước</dt><dd>{e.basis ?? ''}</dd></div>
                </dl></td></tr>
              ) : null}
            </React.Fragment>
          );
        })}</tbody>
      </table>
      <p className="note">Ước theo tốc độ đọc giả định, chưa đo.</p>
      <FullText text={text} />
    </>
  );
};
